import { trpc } from "@/api/trpc";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { env } from "@/env";
import { PageHeader } from "@/shared/components/page-header";
import { PasswordInput } from "@/shared/components/password-input";
import { StatusPill, type StatusPillVariant } from "@/shared/components/status-pill";
import { useHasPermission } from "@/shared/rbac";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  type SetGooglePlacesConfigInput,
  type SetGoogleSyncConfigInput,
  setGooglePlacesConfigSchema,
  setGoogleSyncConfigSchema,
} from "@template/shared";
import { ChevronDown, ChevronRight, Copy, Mail, MailX, RefreshCw, Trash2 } from "lucide-react";
import { type ComponentPropsWithoutRef, forwardRef, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { ProvizniPasmaCard } from "./admin-provize-pasma-card";

/**
 * Administrace → Nástroje. Provozní přepínače, které jde měnit za běhu
 * bez zásahu do konfigurace serveru.
 */
export function AdminToolsPage() {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Nástroje"
        description="Provozní přepínače aplikace. Změna platí okamžitě, bez restartu serveru."
        breadcrumbs={[
          { label: "Přehled", to: "/dashboard" },
          { label: "Administrace" },
          { label: "Nástroje" },
        ]}
        testId="admin-tools-page"
      />
      <ClientEmailsCard />
      <ProvizniPasmaCard />
      <GoogleSyncConfigCard />
      <GoogleDriveConfigCard />
      <GooglePlacesConfigCard />
    </div>
  );
}

/** Jak dlouho worker drží starou hodnotu v cache — musí sedět s apps/worker/src/lib/app-settings.ts. */
const PROPAGATION_SECONDS = 10;

/** Velký přepínač odesílání klientských e-mailů (kill-switch). */
function ClientEmailsCard() {
  const utils = trpc.useUtils();
  const canWrite = useHasPermission("settings.write");
  const [propagatingFor, setPropagatingFor] = useState(0);
  const settingQuery = trpc.admin.settings.getClientEmailsEnabled.useQuery();
  const mutation = trpc.admin.settings.setClientEmailsEnabled.useMutation({
    onSuccess: (result) => {
      toast.success(
        result.enabled
          ? "Odesílání klientských e-mailů je zapnuté"
          : "Odesílání klientských e-mailů je vypnuté",
      );
      utils.admin.settings.getClientEmailsEnabled.invalidate();
      // Worker si hodnotu cachuje — dokud se nepropíše, přepínač zamkneme,
      // ať admin nezkouší přepínat tam a zpět v domnění, že se nic nestalo.
      setPropagatingFor(PROPAGATION_SECONDS);
    },
    onError: (err) => toast.error(err.message),
  });

  useEffect(() => {
    if (propagatingFor <= 0) return;
    const timer = setTimeout(() => setPropagatingFor((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [propagatingFor]);

  if (settingQuery.isPending) {
    return <Skeleton className="h-52 w-full" data-testid="admin-client-emails-loading" />;
  }

  if (settingQuery.isError) {
    return (
      <div
        className="rounded-md bg-destructive/10 p-3 text-sm text-destructive"
        role="alert"
        data-testid="admin-client-emails-error"
      >
        {settingQuery.error.message}
      </div>
    );
  }

  const { enabled, source, updatedAt, updatedBy } = settingQuery.data;
  // `enabled` je vždy reálný stav, kterým se řídí odesílání. `source: "env"`
  // znamená, že hodnota pochází z konfigurace serveru, protože přepínač zatím
  // nikdo nepoužil — první přepnutí ji uloží do DB.
  const isFromEnv = source === "env";
  const willEnable = !enabled;
  const isPropagating = propagatingFor > 0;
  const toggleDisabled = !canWrite || mutation.isPending || isPropagating;

  return (
    <Card data-testid="admin-client-emails-card">
      <CardContent className="space-y-5 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-1">
            <h2 className="text-xl font-semibold tracking-tight">Klientské e-maily</h2>
            <p className="max-w-prose text-sm text-muted-foreground">
              Hlavní vypínač veškeré komunikace směrem ke klientům — potvrzení přihlášek,
              automatické série i kampaně. Interní notifikace týmu běží nezávisle na tomto
              přepínači.
            </p>
          </div>
          <StatusPill
            variant={enabled ? "green" : "red"}
            className="px-4 py-1.5 text-sm"
            testId="admin-client-emails-status"
          >
            {enabled ? "Zapnuto" : "Vypnuto"}
          </StatusPill>
        </div>

        <p className="text-xs text-muted-foreground" data-testid="admin-client-emails-meta">
          {isFromEnv
            ? "(výchozí ze serveru — zatím nepřepnuto v administraci)"
            : `Naposledy změnil ${updatedBy ?? "neznámý uživatel"}${
                updatedAt ? ` — ${formatDatumCas(updatedAt)}` : ""
              }`}
        </p>

        <div className="flex items-center gap-3">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <ToggleButton
                willEnable={willEnable}
                disabled={toggleDisabled}
                canWrite={canWrite}
                propagatingFor={propagatingFor}
              />
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="admin-client-emails-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {willEnable
                    ? "Zapnout odesílání klientských e-mailů?"
                    : "Vypnout odesílání klientských e-mailů?"}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {willEnable
                    ? "Klientům začnou odcházet e-maily — potvrzení přihlášek, automatické série, kampaně. Změna se projeví do ~10 sekund (cache workerů)."
                    : "Žádný klientský e-mail neodejde, pokusy se zaevidují jako selhané. Změna se projeví do ~10 sekund (cache workerů)."}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="admin-client-emails-cancel">
                  Zrušit
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => mutation.mutate({ enabled: willEnable })}
                  data-testid="admin-client-emails-confirm"
                >
                  {willEnable ? "Zapnout" : "Vypnout"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          {isPropagating ? (
            <span
              className="text-xs text-muted-foreground"
              aria-live="polite"
              data-testid="admin-client-emails-propagating"
            >
              Workery si hodnotu načtou do {propagatingFor} s.
            </span>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

interface ToggleButtonProps extends ComponentPropsWithoutRef<"button"> {
  willEnable: boolean;
  canWrite: boolean;
  propagatingFor: number;
}

/**
 * Tlačítko přepínače. Zamčený stav vždy vysvětlí tooltipem — bez oprávnění
 * `settings.write`, nebo dokud se změna nepropíše do workerů. Disabled tlačítko
 * neposílá myší události, proto je tooltip navěšený na obalující span.
 */
const ToggleButton = forwardRef<HTMLButtonElement, ToggleButtonProps>(function ToggleButton(
  { willEnable, canWrite, propagatingFor, disabled, ...rest },
  ref,
) {
  const isPropagating = propagatingFor > 0;
  const button = (
    <Button
      ref={ref}
      variant={willEnable ? "default" : "destructive"}
      disabled={disabled}
      data-testid="admin-client-emails-toggle-button"
      {...rest}
    >
      {isPropagating ? null : willEnable ? (
        <Mail className="mr-2 h-4 w-4" aria-hidden="true" />
      ) : (
        <MailX className="mr-2 h-4 w-4" aria-hidden="true" />
      )}
      {isPropagating
        ? `Propisuje se… (${propagatingFor} s)`
        : willEnable
          ? "Zapnout odesílání"
          : "Vypnout odesílání"}
    </Button>
  );

  if (canWrite && !isPropagating) return button;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex" data-testid="admin-client-emails-toggle-wrapper">
            {button}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          {canWrite
            ? "Změna se propisuje do workerů, počkejte prosím."
            : "Přepínat smí jen administrátor (oprávnění settings.write)."}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
});

/** Karta OAuth klíčů pro synchronizaci kontaktů do Google Contacts. */
function GoogleSyncConfigCard() {
  const configQuery = trpc.admin.settings.getGoogleSyncConfig.useQuery();

  if (configQuery.isPending) {
    return <Skeleton className="h-72 w-full" data-testid="admin-google-sync-loading" />;
  }

  if (configQuery.isError) {
    return (
      <div
        className="rounded-md bg-destructive/10 p-3 text-sm text-destructive"
        role="alert"
        data-testid="admin-google-sync-error"
      >
        {configQuery.error.message}
      </div>
    );
  }

  // Remount obsahu při změně uloženého Client ID (po uložení/smazání) —
  // defaultValues formuláře se jinak samy nepřepíšou.
  return (
    <GoogleSyncConfigContent key={configQuery.data.clientId ?? ""} config={configQuery.data} />
  );
}

interface GoogleSyncConfig {
  configured: boolean;
  source: "db" | "env" | null;
  clientId: string | null;
  secretSet: boolean;
  redirectUri: string;
}

function GoogleSyncConfigContent({ config }: { config: GoogleSyncConfig }) {
  const utils = trpc.useUtils();
  const canWrite = useHasPermission("settings.write");
  const [guideOpen, setGuideOpen] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<SetGoogleSyncConfigInput>({
    resolver: zodResolver(setGoogleSyncConfigSchema),
    defaultValues: { clientId: config.clientId ?? "", clientSecret: "" },
  });

  const saveMutation = trpc.admin.settings.setGoogleSyncConfig.useMutation({
    onSuccess: (result, variables) => {
      toast.success(
        result.accountsPaused > 0
          ? `Klíče uložené. Změna klienta pozastavila ${result.accountsPaused} propojených účtů — uživatelé se musí propojit znovu.`
          : "Klíče Google synchronizace jsou uložené",
      );
      utils.admin.settings.getGoogleSyncConfig.invalidate();
      // Secret z formuláře po uložení vyprázdníme — dál ho drží jen server.
      reset({ clientId: variables.clientId, clientSecret: "" });
    },
    onError: (err) => toast.error(err.message),
  });

  const clearMutation = trpc.admin.settings.clearGoogleSyncConfig.useMutation({
    onSuccess: () => {
      toast.success("Klíče z administrace byly smazány");
      utils.admin.settings.getGoogleSyncConfig.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const onSubmit = handleSubmit((values) => {
    // Secret je povinný, jen dokud není nikde uložený — pak stačí Client ID.
    if (!config.secretSet && !values.clientSecret) {
      setError("clientSecret", { message: "Vyplňte Client Secret" });
      return;
    }
    saveMutation.mutate({
      clientId: values.clientId,
      ...(values.clientSecret ? { clientSecret: values.clientSecret } : {}),
    });
  });

  const copyRedirectUri = async () => {
    try {
      await navigator.clipboard.writeText(config.redirectUri);
      toast.success("Redirect URI zkopírována do schránky");
    } catch {
      toast.error("Kopírování se nepovedlo — zkopírujte adresu ručně");
    }
  };

  const status: { variant: StatusPillVariant; label: string } = !config.configured
    ? { variant: "slate", label: "Nenakonfigurováno" }
    : config.source === "db"
      ? { variant: "green", label: "Nakonfigurováno (z administrace)" }
      : { variant: "blue", label: "Nakonfigurováno (ze serveru)" };

  return (
    <Card data-testid="admin-google-sync-card">
      <CardContent className="space-y-5 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-1">
            <h2 className="text-xl font-semibold tracking-tight">Google synchronizace</h2>
            <p className="max-w-prose text-sm text-muted-foreground">
              Klíče OAuth klienta pro synchronizaci kontaktů do Google Contacts. Uložené klíče platí
              hned — bez zásahu do serveru a bez restartu. Klíče nastavené na serveru slouží jen
              jako záloha, administrace má přednost.
            </p>
          </div>
          <StatusPill
            variant={status.variant}
            className="px-4 py-1.5 text-sm"
            testId="admin-google-sync-status"
          >
            {status.label}
          </StatusPill>
        </div>

        <div className="space-y-2">
          <Label htmlFor="admin-google-sync-redirect-uri">Redirect URI</Label>
          <div className="flex items-center gap-2">
            <Input
              id="admin-google-sync-redirect-uri"
              value={config.redirectUri}
              readOnly
              className="font-mono text-xs"
              data-testid="admin-google-sync-redirect-uri"
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={copyRedirectUri}
              aria-label="Zkopírovat Redirect URI"
              data-testid="admin-google-sync-copy-button"
            >
              <Copy className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Tuto adresu vložíte v Google Cloud Console do „Authorized redirect URIs" (krok 4 návodu
            níže).
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4" data-testid="admin-google-sync-form">
          <div className="space-y-2">
            <Label htmlFor="admin-google-sync-client-id">Client ID</Label>
            <Input
              id="admin-google-sync-client-id"
              placeholder="např. 1234567890-abc123.apps.googleusercontent.com"
              disabled={!canWrite}
              aria-describedby={errors.clientId ? "admin-google-sync-client-id-error" : undefined}
              data-testid="admin-google-sync-client-id-input"
              {...register("clientId")}
            />
            {errors.clientId ? (
              <p
                id="admin-google-sync-client-id-error"
                className="text-sm text-destructive"
                role="alert"
                data-testid="admin-google-sync-client-id-error"
              >
                {errors.clientId.message}
              </p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="admin-google-sync-client-secret">Client Secret</Label>
            <PasswordInput
              id="admin-google-sync-client-secret"
              placeholder={
                config.secretSet ? "••••••• (uloženo — vyplňte jen při změně)" : "např. GOCSPX-…"
              }
              disabled={!canWrite}
              autoComplete="off"
              aria-describedby={
                errors.clientSecret ? "admin-google-sync-client-secret-error" : undefined
              }
              toggleTestId="admin-google-sync-client-secret-toggle"
              data-testid="admin-google-sync-client-secret-input"
              {...register("clientSecret")}
            />
            {errors.clientSecret ? (
              <p
                id="admin-google-sync-client-secret-error"
                className="text-sm text-destructive"
                role="alert"
                data-testid="admin-google-sync-client-secret-error"
              >
                {errors.clientSecret.message}
              </p>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="submit"
              disabled={!canWrite || saveMutation.isPending}
              data-testid="admin-google-sync-save-button"
            >
              {saveMutation.isPending ? "Ukládám…" : "Uložit klíče"}
            </Button>
            {config.source === "db" ? (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    type="button"
                    variant="destructive"
                    disabled={!canWrite || clearMutation.isPending}
                    data-testid="admin-google-sync-clear-button"
                  >
                    <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
                    Smazat klíče
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="admin-google-sync-clear-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Smazat klíče Google synchronizace?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Klíče uložené v administraci se odstraní. Pokud jsou na serveru nastavené
                      záložní klíče, synchronizace pojede dál přes ně; jinak se propojení s Googlem
                      zastaví, dokud klíče znovu nevyplníte. Změna se projeví do ~10 sekund.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="admin-google-sync-clear-cancel">
                      Zrušit
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => clearMutation.mutate()}
                      data-testid="admin-google-sync-clear-confirm"
                    >
                      Smazat
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ) : null}
          </div>
        </form>

        <div className="space-y-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="-ml-2 text-muted-foreground"
            onClick={() => setGuideOpen((o) => !o)}
            aria-expanded={guideOpen}
            data-testid="admin-google-sync-guide-toggle"
          >
            {guideOpen ? (
              <ChevronDown className="mr-1 h-4 w-4" aria-hidden="true" />
            ) : (
              <ChevronRight className="mr-1 h-4 w-4" aria-hidden="true" />
            )}
            Jak klíče získat (krok za krokem)
          </Button>
          {guideOpen ? (
            <ol
              className="list-decimal space-y-2 pl-8 text-sm text-muted-foreground"
              data-testid="admin-google-sync-guide"
            >
              <li>
                Otevřete <span className="font-mono">console.cloud.google.com</span>, přihlaste se
                firemním Google účtem a vytvořte nový projekt (nebo vyberte existující).
              </li>
              <li>
                V menu „APIs &amp; Services → Library" vyhledejte „People API" a klikněte na
                „Enable".
              </li>
              <li>
                V „APIs &amp; Services → OAuth consent screen" zvolte typ „External", vyplňte název
                aplikace a kontaktní e-mail. Do „Test users" přidejte e-maily všech, kdo budou
                synchronizaci používat.
              </li>
              <li>
                V „APIs &amp; Services → Credentials" klikněte na „Create credentials → OAuth client
                ID", vyberte typ „Web application" a do „Authorized redirect URIs" vložte adresu,
                kterou zkopírujete tlačítkem výše.
              </li>
              <li>
                Google zobrazí Client ID a Client Secret — zkopírujte je do polí na této kartě a
                uložte.
              </li>
            </ol>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Karta firemního Google Drive pro modul Dokumenty. Používá STEJNÉ OAuth
 * klíče jako Google synchronizace (karta výš) — jen jiný scope a vlastní
 * callback, který je potřeba přidat v Google konzoli jako druhou redirect URI.
 */
function GoogleDriveConfigCard() {
  const utils = trpc.useUtils();
  const canWrite = useHasPermission("settings.write");
  const statusQuery = trpc.hca.dokumenty.getStatus.useQuery();

  useDriveOAuthResultToast();

  const disconnectMutation = trpc.hca.dokumenty.disconnect.useMutation({
    onSuccess: () => {
      toast.success("Firemní Google Drive odpojen");
      utils.hca.dokumenty.getStatus.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  if (statusQuery.isPending) {
    return <Skeleton className="h-72 w-full" data-testid="admin-google-drive-loading" />;
  }

  if (statusQuery.isError) {
    return (
      <div
        className="rounded-md bg-destructive/10 p-3 text-sm text-destructive"
        role="alert"
        data-testid="admin-google-drive-error"
      >
        {statusQuery.error.message}
      </div>
    );
  }

  const status = statusQuery.data;

  const copyRedirectUri = async () => {
    try {
      await navigator.clipboard.writeText(status.redirectUri);
      toast.success("Redirect URI zkopírována do schránky");
    } catch {
      toast.error("Kopírování se nepovedlo — zkopírujte adresu ručně");
    }
  };

  return (
    <Card data-testid="admin-google-drive-card">
      <CardContent className="space-y-5 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-1">
            <h2 className="text-xl font-semibold tracking-tight">Google Drive dokumenty</h2>
            <p className="max-w-prose text-sm text-muted-foreground">
              Firemní úložiště pro modul Dokumenty — soubory se nahrávají do složky „HCA ERP" na
              propojeném Google účtu (podsložky podle klienta či kategorie), na serveru aplikace se
              nic neukládá. Používá stejné OAuth klíče jako Google synchronizace výše.
            </p>
          </div>
          <StatusPill
            variant={status.connected ? "green" : "slate"}
            className="px-4 py-1.5 text-sm"
            testId="admin-google-drive-status"
          >
            {status.connected ? `Připojeno jako ${status.googleEmail ?? "?"}` : "Nepřipojeno"}
          </StatusPill>
        </div>

        <p className="text-sm text-muted-foreground" data-testid="admin-google-drive-root-info">
          Kořen:{" "}
          {status.parentFolderId ? (
            <>
              složka{" "}
              <a
                href={`https://drive.google.com/drive/folders/${status.parentFolderId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono underline underline-offset-2"
                data-testid="admin-google-drive-parent-folder-link"
              >
                {status.parentFolderId}
              </a>{" "}
              (sdílený disk / vlastní složka)
            </>
          ) : (
            "Můj disk připojeného účtu"
          )}
        </p>

        <div className="space-y-2">
          <Label htmlFor="admin-google-drive-redirect-uri">Druhá redirect URI (pro Drive)</Label>
          <div className="flex items-center gap-2">
            <Input
              id="admin-google-drive-redirect-uri"
              value={status.redirectUri}
              readOnly
              className="font-mono text-xs"
              data-testid="admin-google-drive-redirect-uri"
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={copyRedirectUri}
              aria-label="Zkopírovat Redirect URI pro Google Drive"
              data-testid="admin-google-drive-copy-button"
            >
              <Copy className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Tuto adresu přidejte v Google Cloud Console ke stejnému OAuth klientovi jako
            synchronizaci — „Authorized redirect URIs" musí obsahovat obě adresy. Zapněte také
            „Google Drive API" v „APIs &amp; Services → Library".
          </p>
        </div>

        <DriveParentFolderSection
          key={status.parentFolderId ?? "muj-disk"}
          parentFolderId={status.parentFolderId}
          canWrite={canWrite}
        />

        <div className="flex flex-wrap items-center gap-3">
          {status.connected ? (
            <>
              {status.folderLink ? (
                <Button asChild variant="outline" data-testid="admin-google-drive-folder-link">
                  <a href={status.folderLink} target="_blank" rel="noopener noreferrer">
                    Otevřít složku „HCA ERP"
                  </a>
                </Button>
              ) : null}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    type="button"
                    variant="destructive"
                    disabled={!canWrite || disconnectMutation.isPending}
                    data-testid="admin-google-drive-disconnect-button"
                  >
                    <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
                    Odpojit
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="admin-google-drive-disconnect-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Odpojit firemní Google Drive?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Soubory na Drive i evidence dokumentů zůstávají, jen nepůjde nahrávat nové
                      dokumenty, dokud se Drive znovu nepřipojí. Odkazy v odeslaných e-mailech
                      fungují dál.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="admin-google-drive-disconnect-cancel">
                      Zrušit
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => disconnectMutation.mutate()}
                      data-testid="admin-google-drive-disconnect-confirm"
                    >
                      Odpojit
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          ) : (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <Button
                      type="button"
                      disabled={!canWrite || !status.configured}
                      onClick={() => {
                        // Plný redirect — OAuth consent jede přes API a Google.
                        window.location.assign(`${env.VITE_API_URL}/api/google-drive/connect`);
                      }}
                      data-testid="admin-google-drive-connect-button"
                    >
                      Připojit Google Drive
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {!status.configured
                    ? "Nejdřív uložte OAuth klíče v kartě Google synchronizace výše."
                    : canWrite
                      ? "Otevře souhlas Googlu pro firemní účet."
                      : "Připojit smí jen administrátor (oprávnění settings.write)."}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        <p className="text-xs text-muted-foreground" data-testid="admin-google-drive-consent-note">
          {status.connected
            ? "Bylo-li propojení vytvořeno dříve (s užším oprávněním jen na soubory aplikace), pro ukládání na sdílený disk Drive odpojte a připojte znovu — připojení vyžaduje nový souhlas (rozšířený přístup k Disku kvůli sdíleným diskům)."
            : "Připojení vyžaduje nový souhlas (rozšířený přístup k Disku kvůli sdíleným diskům)."}
        </p>
      </CardContent>
    </Card>
  );
}

interface DriveParentFolderSectionProps {
  parentFolderId: string | null;
  canWrite: boolean;
}

/**
 * Pole „ID nadřazené složky" — kořen „HCA ERP" se zakládá POD ní (typicky
 * složka „HCA MORAVIA" na sdíleném disku). Remount přes `key` v rodiči,
 * ať se input přednastaví po uložení/refetchi (vzor Google Places karty).
 */
function DriveParentFolderSection({ parentFolderId, canWrite }: DriveParentFolderSectionProps) {
  const utils = trpc.useUtils();
  const [value, setValue] = useState(parentFolderId ?? "");

  const saveMutation = trpc.hca.dokumenty.setParentFolder.useMutation({
    onSuccess: (data) => {
      toast.success(
        data.connected
          ? "Nadřazená složka uložena — složka „HCA ERP“ je připravená pod novým umístěním."
          : "Nadřazená složka uložena — použije se při připojení Google Drive.",
      );
      utils.hca.dokumenty.getStatus.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  return (
    <div className="space-y-2">
      <Label htmlFor="admin-google-drive-parent-folder">ID nadřazené složky (volitelné)</Label>
      <div className="flex items-center gap-2">
        <Input
          id="admin-google-drive-parent-folder"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={!canWrite || saveMutation.isPending}
          placeholder="např. 1AbCdEfGhIjKlMnOpQrStUv"
          className="font-mono text-xs"
          data-testid="admin-google-drive-parent-folder-input"
        />
        <Button
          type="button"
          variant="outline"
          disabled={!canWrite || saveMutation.isPending}
          onClick={() => saveMutation.mutate({ parentFolderId: value })}
          data-testid="admin-google-drive-parent-folder-save-button"
        >
          Uložit
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Otevřete cílovou složku (např. „HCA MORAVIA" na sdíleném disku) v prohlížeči a zkopírujte ID
        z adresy — část za <code>/folders/</code>. Prázdné = Můj disk připojeného účtu. Při změně
        dosavadní soubory zůstávají na původním místě; nové se ukládají do nové složky.
      </p>
    </div>
  );
}

/**
 * Google po souhlasu redirectuje na `/dashboard/admin/nastroje?drive=…`.
 * Výsledek ukážeme toastem a parametr z URL uklidíme (vzor google-sync-card).
 */
function useDriveOAuthResultToast(): void {
  const utils = trpc.useUtils();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get("drive");
    if (!result) return;

    if (result === "ok") {
      toast.success("Firemní Google Drive připojen — složka „HCA ERP“ je založená.");
      utils.hca.dokumenty.getStatus.invalidate();
    } else if (result === "zruseno") {
      toast.info("Připojení Google Drive jste zrušili.");
    } else if (result === "nenakonfigurovano") {
      toast.error("Nejdřív uložte OAuth klíče v kartě Google synchronizace.");
    } else if (result === "slozka-nenalezena") {
      toast.error(
        "Složka nenalezena — zkontrolujte ID nadřazené složky a že má připojený účet přístup.",
      );
    } else {
      toast.error("Připojení Google Drive se nezdařilo. Zkuste to prosím znovu.");
    }

    params.delete("drive");
    const query = params.toString();
    window.history.replaceState(null, "", `${window.location.pathname}${query ? `?${query}` : ""}`);
  }, [utils]);
}

/** Karta Google Places API — živé hodnocení a počet recenzí na veřejném webu. */
function GooglePlacesConfigCard() {
  const configQuery = trpc.admin.settings.getGooglePlacesConfig.useQuery();

  if (configQuery.isPending) {
    return <Skeleton className="h-72 w-full" data-testid="admin-google-places-loading" />;
  }

  if (configQuery.isError) {
    return (
      <div
        className="rounded-md bg-destructive/10 p-3 text-sm text-destructive"
        role="alert"
        data-testid="admin-google-places-error"
      >
        {configQuery.error.message}
      </div>
    );
  }

  // Remount obsahu při změně uloženého Place ID (po uložení/smazání) —
  // defaultValues formuláře se jinak samy nepřepíšou.
  return (
    <GooglePlacesConfigContent key={configQuery.data.placeId ?? ""} config={configQuery.data} />
  );
}

interface GooglePlacesLastRating {
  rating: number;
  count: number;
  fetchedAt: string;
}

interface GooglePlacesConfig {
  configured: boolean;
  placeId: string | null;
  keySet: boolean;
  lastRating: GooglePlacesLastRating | null;
}

function GooglePlacesConfigContent({ config }: { config: GooglePlacesConfig }) {
  const utils = trpc.useUtils();
  const canWrite = useHasPermission("settings.write");
  const [guideOpen, setGuideOpen] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<SetGooglePlacesConfigInput>({
    resolver: zodResolver(setGooglePlacesConfigSchema),
    defaultValues: { placeId: config.placeId ?? "", apiKey: "" },
  });

  const saveMutation = trpc.admin.settings.setGooglePlacesConfig.useMutation({
    onSuccess: (_result, variables) => {
      toast.success("Konfigurace Google recenzí je uložená");
      utils.admin.settings.getGooglePlacesConfig.invalidate();
      // Klíč z formuláře po uložení vyprázdníme — dál ho drží jen server.
      reset({ placeId: variables.placeId, apiKey: "" });
    },
    onError: (err) => toast.error(err.message),
  });

  const testMutation = trpc.admin.settings.testGooglePlacesConfig.useMutation({
    onSuccess: (result) => {
      toast.success(
        `${formatGoogleRating(result.rating)} ★ · ${formatRecenzeCount(result.count)}, načteno teď`,
      );
      utils.admin.settings.getGooglePlacesConfig.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const clearMutation = trpc.admin.settings.clearGooglePlacesConfig.useMutation({
    onSuccess: () => {
      toast.success("Konfigurace Google recenzí byla smazána");
      utils.admin.settings.getGooglePlacesConfig.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const onSubmit = handleSubmit((values) => {
    // Klíč je povinný, jen dokud není uložený — pak stačí Place ID.
    if (!config.keySet && !values.apiKey) {
      setError("apiKey", { message: "Vyplňte API klíč" });
      return;
    }
    saveMutation.mutate({
      placeId: values.placeId,
      ...(values.apiKey ? { apiKey: values.apiKey } : {}),
    });
  });

  return (
    <Card data-testid="admin-google-places-card">
      <CardContent className="space-y-5 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-1">
            <h2 className="text-xl font-semibold tracking-tight">Google recenze</h2>
            <p className="max-w-prose text-sm text-muted-foreground">
              Živé hodnocení a počet recenzí z Googlu na úvodní stránce webu. Hodnota se obnovuje
              zhruba jednou denně; dokud není nic nakonfigurováno, web ukazuje statická čísla.
            </p>
          </div>
          <StatusPill
            variant={config.configured ? "green" : "slate"}
            className="px-4 py-1.5 text-sm"
            testId="admin-google-places-status"
          >
            {config.configured ? "Nakonfigurováno" : "Nenakonfigurováno"}
          </StatusPill>
        </div>

        {config.lastRating ? (
          <p
            className="text-xs text-muted-foreground"
            data-testid="admin-google-places-last-rating"
          >
            Poslední načtená hodnota: {formatGoogleRating(config.lastRating.rating)} ★ (
            {formatRecenzeCount(config.lastRating.count)}) —{" "}
            {formatDatumCas(config.lastRating.fetchedAt)}
          </p>
        ) : null}

        <form onSubmit={onSubmit} className="space-y-4" data-testid="admin-google-places-form">
          <div className="space-y-2">
            <Label htmlFor="admin-google-places-api-key">API klíč</Label>
            <PasswordInput
              id="admin-google-places-api-key"
              placeholder={
                config.keySet ? "••••••• (uloženo — vyplňte jen při změně)" : "např. AIzaSy…"
              }
              disabled={!canWrite}
              autoComplete="off"
              aria-describedby={errors.apiKey ? "admin-google-places-api-key-error" : undefined}
              toggleTestId="admin-google-places-api-key-toggle"
              data-testid="admin-google-places-api-key-input"
              {...register("apiKey")}
            />
            {errors.apiKey ? (
              <p
                id="admin-google-places-api-key-error"
                className="text-sm text-destructive"
                role="alert"
                data-testid="admin-google-places-api-key-error"
              >
                {errors.apiKey.message}
              </p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="admin-google-places-place-id">Place ID</Label>
            <Input
              id="admin-google-places-place-id"
              placeholder="např. ChIJN1t_tDeuEmsRUsoyG83frY4"
              disabled={!canWrite}
              aria-describedby={
                errors.placeId
                  ? "admin-google-places-place-id-error"
                  : "admin-google-places-place-id-help"
              }
              data-testid="admin-google-places-place-id-input"
              {...register("placeId")}
            />
            {errors.placeId ? (
              <p
                id="admin-google-places-place-id-error"
                className="text-sm text-destructive"
                role="alert"
                data-testid="admin-google-places-place-id-error"
              >
                {errors.placeId.message}
              </p>
            ) : null}
            <p id="admin-google-places-place-id-help" className="text-xs text-muted-foreground">
              Identifikátor místa na Google Mapách. Najdete ho přes{" "}
              <a
                href="https://developers.google.com/maps/documentation/places/web-service/place-id"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2 hover:text-foreground"
                data-testid="admin-google-places-place-id-finder-link"
              >
                Place ID Finder
              </a>{" "}
              — vyhledejte „HCA Moravia Brno".
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="submit"
              disabled={!canWrite || saveMutation.isPending}
              data-testid="admin-google-places-save-button"
            >
              {saveMutation.isPending ? "Ukládám…" : "Uložit"}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={!canWrite || !config.configured || testMutation.isPending}
              onClick={() => testMutation.mutate()}
              title="Testuje se uložená konfigurace — neuložené změny ve formuláři nejdřív uložte."
              data-testid="admin-google-places-test-button"
            >
              <RefreshCw
                className={`mr-2 h-4 w-4${testMutation.isPending ? " animate-spin" : ""}`}
                aria-hidden="true"
              />
              {testMutation.isPending ? "Testuji…" : "Otestovat"}
            </Button>
            {config.keySet || config.placeId !== null ? (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    type="button"
                    variant="destructive"
                    disabled={!canWrite || clearMutation.isPending}
                    data-testid="admin-google-places-clear-button"
                  >
                    <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
                    Smazat
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="admin-google-places-clear-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Smazat konfiguraci Google recenzí?</AlertDialogTitle>
                    <AlertDialogDescription>
                      API klíč, Place ID i poslední načtená hodnota se odstraní. Web se vrátí ke
                      statickým číslům, dokud konfiguraci znovu nevyplníte.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="admin-google-places-clear-cancel">
                      Zrušit
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => clearMutation.mutate()}
                      data-testid="admin-google-places-clear-confirm"
                    >
                      Smazat
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ) : null}
          </div>
        </form>

        <div className="space-y-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="-ml-2 text-muted-foreground"
            onClick={() => setGuideOpen((o) => !o)}
            aria-expanded={guideOpen}
            data-testid="admin-google-places-guide-toggle"
          >
            {guideOpen ? (
              <ChevronDown className="mr-1 h-4 w-4" aria-hidden="true" />
            ) : (
              <ChevronRight className="mr-1 h-4 w-4" aria-hidden="true" />
            )}
            Jak klíč a Place ID získat (krok za krokem)
          </Button>
          {guideOpen ? (
            <ol
              className="list-decimal space-y-2 pl-8 text-sm text-muted-foreground"
              data-testid="admin-google-places-guide"
            >
              <li>
                Otevřete <span className="font-mono">console.cloud.google.com</span> a vyberte
                stejný projekt jako u Google synchronizace (nebo vytvořte nový).
              </li>
              <li>
                V menu „APIs &amp; Services → Library" vyhledejte „Places API (New)" a klikněte na
                „Enable".
              </li>
              <li>
                V „APIs &amp; Services → Credentials" klikněte na „Create credentials → API key".
                Doporučujeme klíč hned omezit („Restrict key") jen na Places API (New).
              </li>
              <li>Vygenerovaný klíč vložte do pole „API klíč" výše.</li>
              <li>
                Place ID najdete přes{" "}
                <a
                  href="https://developers.google.com/maps/documentation/places/web-service/place-id"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 hover:text-foreground"
                  data-testid="admin-google-places-guide-finder-link"
                >
                  Place ID Finder
                </a>{" "}
                — vyhledejte „HCA Moravia Brno" a zkopírujte hodnotu do pole „Place ID".
              </li>
              <li>
                Uložte a klikněte na „Otestovat". Hodnota se pak obnovuje zhruba jednou denně, což
                se bezpečně vejde do měsíčního free kreditu Googlu.
              </li>
            </ol>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

/** Rating česky s čárkou a jedním desetinným místem — 4.9 → „4,9". */
function formatGoogleRating(rating: number): string {
  return rating.toLocaleString("cs-CZ", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/** Správné skloňování počtu recenzí: 1 recenze / 2–4 recenze / 5+ recenzí. */
function formatRecenzeCount(count: number): string {
  if (count === 1) return "1 recenze";
  if (count >= 2 && count <= 4) return `${count} recenze`;
  return `${count} recenzí`;
}

function formatDatumCas(iso: string): string {
  return new Date(iso).toLocaleString("cs-CZ", {
    day: "numeric",
    month: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
